"use client";
import { useState, useEffect, useRef } from "react";
import { ConnectionState } from "livekit-client";
import { useConnectionState } from "@livekit/components-react";

/**
 * Tracks how long the current call has been active.
 *
 * Starts counting when the LiveKit room reaches ConnectionState.Connected.
 * Works for every participant independently — no server-side sync needed.
 */
export const useCallDuration = (callStartedAt?: string | null) => {
  const connectionState = useConnectionState();
  const startTimeRef = useRef<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (connectionState !== ConnectionState.Connected) return;

    const startTime = callStartedAt ? new Date(callStartedAt).getTime() : Date.now();

    startTimeRef.current = startTime;

    // Calculate immediately
    setElapsed(Math.floor((Date.now() - startTime) / 1000));

    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [connectionState, callStartedAt]);

  /**
   * Format seconds into HH:MM:SS (or MM:SS when < 1 hour).
   */
  const formatDuration = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return {
    elapsed,
    formattedDuration: formatDuration(elapsed),
    isConnected: connectionState === ConnectionState.Connected
  };
};
